import React, { useState, useEffect, useRef } from 'react';
import logo256 from '../assets/logo_256.png'
import ColorUtils from '../utils/colors'
import localization from '../utils/localization'
import MainTab from '../components/MainTab'
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import {
  Menu, FormControl, InputLabel, Select, OutlinedInput, MenuItem, Divider, Dialog, DialogTitle, DialogContent,
  Button, CircularProgress, ButtonBase, Stack, Paper, Box, Typography, Fade
} from '@mui/material';
import './Main.css'
import ServerUtils from '../utils/server'
import UserUtils from '../utils/user'
import toast from 'react-hot-toast'
import MasterPassUtils from '../utils/masterpass'
import FormInput from '../components/FormInput'
import AesUtils from '../utils/aes'
import ContentUtils from '../utils/content'
import ConstUtils from '../utils/const'
import ModalUserInfo from '../components/ModalUserInfo'
import ModalHelp from '../components/ModalHelp'
import ModalFeedback from '../components/ModalFeedback'
import ModalChangeMasterPassword from '../components/ModalChangeMasterPassword'
import ModalDashboard from '../components/ModalDashboard'
import binanceLogo from '../assets/binance.png'
import wechatLogo from '../assets/social-wechat.png'
import { initializePaddle } from '@paddle/paddle-js'

function Main() {
  const navigate = useNavigate();
  let location = useLocation();
  const [admin, setAdmin] = useState(false)
  const [userInfoDialogOpen, setUserInfoDialogOpen] = useState(false)
  const [helpDialogOpen, setHelpDialogOpen] = useState(false)
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false)
  const [changeMasterPasswordDialogOpen, setChangeMasterPasswordDialogOpen] = useState(false)
  const [dashboardDialogOpen, setDashboardDialogOpen] = useState(false)

  const [masterPassOpen, setMasterPassOpen] = useState(false)
  const [masterPassword, setMasterPassword] = useState('')
  const [masterPasswordErr, setMasterPasswordErr] = useState('')

  // paddle.js instance (initialize once per page)
  const paddleRef = useRef(null)

  // user info
  const [userInfo, setUserInfo] = useState({})
  useEffect(() => {
    ServerUtils.userInfo().then(async (resp) => {
      if (!resp.ok) {
        navigate('/')
        return
      }
      let respJson = await resp.json()
      if (respJson.errno !== 0) {
        toast.error(ServerUtils.handleError(respJson))
        return
      }
      let userInfo = respJson.data
      UserUtils.setUserInfo(userInfo)
      setUserInfo(userInfo)
      let masterPass = MasterPassUtils.getMasterPass(userInfo.account)
      if (masterPass) {
        UserUtils.setMasterPass(masterPass)
        ContentUtils.getContent()
        navigate(`/main.html/content?lang=${localization.language.lang}`)
      } else {
        setMasterPassOpen(true)
      }
    })

    ServerUtils.admin().then(async (resp) => {
      if (!resp.ok) {
        return
      }
      let respJson = await resp.json()
      if (respJson.errno !== 0) {
        return
      }
      setAdmin(true)
    })
  }, [])

  function logout() {
    ServerUtils.logout().then(async (resp) => {
      navigate(`/login.html?lang=${localization.language.lang}`)
    })
  }

  function checkMasterPass() {
    if (masterPassword === '') {
      setMasterPasswordErr(localization.strings.required)
      return
    }
    let decryptedAccount = AesUtils.decryptData(userInfo.account, masterPassword, userInfo.signed_account)
    if (decryptedAccount !== userInfo.account) {
      setMasterPasswordErr(localization.strings.errorMasterPassword)
      return
    }
    setMasterPassOpen(false)
    UserUtils.setMasterPass(masterPassword)
    ContentUtils.getContent()
    navigate(`/main.html/content?lang=${localization.language.lang}`)
  }

  // menu
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // payjs
  const [payStep, setPayStep] = useState(0)
  const [payUrl, setPayUrl] = useState(false)
  const [payTitle, setPayTitle] = useState('')
  const [payPrice, setPayPrice] = useState('')
  const [payHint, setPayHint] = useState('')
  function loadBinancePayUrl() {
    if (payStep > 1) return
    setPayStep(2)
    setPayUrl('')
    let userInfo = UserUtils.getUserInfo()
    ServerUtils.postBinancePay({
      platform_type: '',
      app_id: ConstUtils.appID,
      account: userInfo.account,
    }).then(async (resp) => {
      if (!resp.ok) {
        try {
          let respJson = await resp.json()
          if (respJson.errno !== 0) {
            toast.error(ServerUtils.handleError(respJson))
            return
          }
        } catch (e) {

        }
        toast.error(localization.strings.networkErr)
        return
      }
      let respJson = await resp.json()
      if (respJson.errno !== 0) {
        toast.error(ServerUtils.handleError(respJson))
        return
      }
      setPayTitle(localization.strings.easypassBinancePayTitle)
      setPayPrice(localization.strings.easypassBinancePrice)
      setPayHint(localization.strings.easypassBinancePrice)
      setPayUrl(respJson.data.qrcodeLink)
      setPayStep(3)
    })
  }
  function loadWechatPayUrl() {
    if (payStep > 1) return
    setPayStep(2)
    setPayUrl('')
    let userInfo = UserUtils.getUserInfo()
    ServerUtils.postPayJs({
      payjs_type: '',
      app_id: ConstUtils.appID,
      account: userInfo.account,
      body: localization.strings.easypassBody,
    }).then(async (resp) => {
      if (!resp.ok) {
        try {
          let respJson = await resp.json()
          if (respJson.errno !== 0) {
            toast.error(ServerUtils.handleError(respJson))
            return
          }
        } catch (e) {

        }
        toast.error(localization.strings.networkErr)
        return
      }
      let respJson = await resp.json()
      if (respJson.errno !== 0) {
        toast.error(ServerUtils.handleError(respJson))
        return
      }
      setPayTitle(localization.strings.easypassWechatPayTitle)
      setPayPrice(localization.strings.easypassWechatPrice)
      setPayHint(localization.strings.easypassWechatPrice)
      setPayUrl(respJson.data.qrcode)
      setPayStep(3)
    })
  }

  async function loadPaddlePayUrl() {
    if (payStep > 1) return
    setPayStep(2)
    try {
      const userInfo = UserUtils.getUserInfo()

      // Initialize Paddle only once; reuse the cached instance on subsequent calls
      if (!paddleRef.current) {
        paddleRef.current = await initializePaddle({
          token: 'live_f7aab74c4400e53a2c9b8bb10cb',
          eventCallback: (event) => {
            if (event.name === 'checkout.completed') {
              setPayStep(0)
              ServerUtils.userInfo().then(async (resp) => {
                if (!resp.ok) return
                let respJson = await resp.json()
                if (respJson.errno !== 0) return
                UserUtils.setUserInfo(respJson.data)
                setUserInfo(respJson.data)
                toast.success(localization.strings.payed)
              })
            }
          },
        })
      }

      paddleRef.current?.Checkout.open({
        settings: {
          displayMode: 'overlay',
          theme: 'light',
          locale: localization.language.lang === 'zh' ? 'zh-Hans' : 'en',
          allowLogout: false,
        },
        items: [
          {
            priceId: 'pri_01kte138dn5n3884agggdpmnjr',
            quantity: 1,
          },
        ],
        customer: {
          email: userInfo.account,
        },
        customData: {
          account: userInfo.account,
        },
      })
      setPayStep(0)
    } catch (e) {
      console.error('Paddle init error:', e)
      toast.error(localization.strings.networkErr)
      setPayStep(1)
    }
  }

  return (
    <div style={{
      height: '100%',
      backgroundColor: ColorUtils.white,
      display: 'flex',
      flexDirection: 'column',
    }}>

      { /* Header */}
      <Box
        component="header"
        sx={{
          height: '4.5rem',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          px: { xs: 2, md: 4 },
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid rgba(0, 0, 0, 0.05)`,
          position: 'sticky',
          top: 0,
          zIndex: 1100,
        }}
      >
        <Box
          component="a"
          href="/"
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            textDecoration: 'none',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'scale(1.02)' }
          }}
        >
          <Box
            sx={{
              p: '2px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #1E88E5 0%, #1565C0 100%)',
              boxShadow: '0 4px 10px rgba(30, 136, 229, 0.2)',
              display: 'flex',
            }}
          >
            <Box
              component="img"
              src={logo256}
              alt="EasyPass Logo"
              sx={{
                display: 'block',
                height: '2.4rem',
                width: '2.4rem',
                borderRadius: '8px',
                backgroundColor: '#fff',
                p: '1px',
              }}
            />
          </Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: '#1E293B',
              ml: 1.5,
              letterSpacing: '-0.02em',
              fontSize: '1.25rem',
              fontFamily: "'Poppins', sans-serif",
              display: { xs: 'none', sm: 'block' }
            }}
          >
            EasyPass
          </Typography>
        </Box>

        <Box sx={{ width: { xs: '1rem', md: '4rem' } }} />

        <Stack direction="row" spacing={1} sx={{ height: '100%' }}>
          <MainTab
            icon="icon-all"
            text={localization.strings.allContent}
            onClick={() => navigate(`/main.html/content?lang=${localization.language.lang}`)}
            active={location.pathname === '/main.html/content'}
          />
          <MainTab
            icon="icon-envelope"
            text={localization.strings.encryptedMessage}
            onClick={() => navigate(`/main.html/message?lang=${localization.language.lang}`)}
            active={location.pathname === '/main.html/message'}
          />
        </Stack>

        <Box sx={{ flex: 1 }} />

        <Stack direction="row" alignItems="center" spacing={2}>
          <FormControl size="small" sx={{ minWidth: 100, display: { xs: 'none', md: 'block' } }}>
            <Select
              value={localization.language.lang || 'en'}
              onChange={(event) => {
                ServerUtils.updateLanguage({ language: event.target.value }).then(async (resp) => {
                  if (!resp.ok) return;
                  let respJson = await resp.json();
                  if (respJson.errno !== 0) return;
                  window.location.href = '?lang=' + event.target.value;
                });
              }}
              sx={{
                color: '#1E293B',
                fontWeight: 700,
                fontSize: '0.85rem',
                borderRadius: '10px',
                backgroundColor: 'rgba(0,0,0,0.04)',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                height: '2.4rem',
              }}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="zh">中文</MenuItem>
            </Select>
          </FormControl>

          <Box
            id="easypass-avatar"
            onClick={(event) => setAnchorEl(event.currentTarget)}
            sx={{
              height: '2.8rem',
              pl: 0.8,
              pr: 2,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              borderRadius: '2rem',
              cursor: 'pointer',
              backgroundColor: open ? '#1E88E5' : 'rgba(0,0,0,0.04)',
              color: open ? '#fff' : '#1E293B',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              border: open ? '1px solid #1E88E5' : '1px solid transparent',
              '&:hover': {
                backgroundColor: open ? '#1E88E5' : 'rgba(0,0,0,0.06)',
                transform: 'translateY(-1px)',
              }
            }}
          >
            <Box
              sx={{
                width: '2.2rem',
                height: '2.2rem',
                borderRadius: '50%',
                backgroundColor: open ? 'rgba(255,255,255,0.2)' : '#1E88E5',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 1.5,
                transition: 'all 0.3s ease',
              }}
            >
              <i className="iconfont icon-person" style={{ fontSize: '1.1rem' }}></i>
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                fontSize: '0.9rem',
                maxWidth: '120px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                display: { xs: 'none', sm: 'block' }
              }}
            >
              {userInfo.account}
            </Typography>
          </Box>
        </Stack>

        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          PaperProps={{
            elevation: 0,
            sx: {
              mt: 1.5,
              borderRadius: '20px',
              minWidth: 220,
              p: 1,
              filter: 'drop-shadow(0px 15px 35px rgba(0,0,0,0.12))',
              border: '1px solid rgba(0,0,0,0.06)',
              overflow: 'visible',
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 24,
                width: 12,
                height: 12,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {[
            { icon: 'icon-payment', label: localization.strings.goPay, color: '#F59E0B', action: () => setPayStep(1) },
            { icon: 'icon-person', label: localization.strings.userInfo, color: '#1E88E5', action: () => setUserInfoDialogOpen(true) },
            { icon: 'icon-lock', label: localization.strings.changeMasterPassword, color: '#6366F1', action: () => setChangeMasterPasswordDialogOpen(true) },
            { icon: 'icon-help', label: localization.strings.help, color: '#10B981', action: () => setHelpDialogOpen(true) },
            { icon: 'icon-chat', label: localization.strings.feedback, color: '#EC4899', action: () => setFeedbackDialogOpen(true) },
            { isDivider: true },
            ...(admin ? [{ icon: 'icon-dashboard', label: 'Dashboard', color: '#8B5CF6', action: () => setDashboardDialogOpen(true) }] : []),
            { icon: 'icon-logout', label: localization.strings.logout, color: '#EF4444', action: logout, isLogout: true },
          ].map((item, idx) => (
            item.isDivider ? <Divider key={idx} sx={{ my: 1, opacity: 0.6 }} /> : (
              <MenuItem
                key={idx}
                onClick={() => {
                  item.action();
                  setAnchorEl(null);
                }}
                sx={{
                  borderRadius: '12px',
                  py: 1.2,
                  px: 1.5,
                  mb: 0.5,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: item.isLogout ? 'rgba(239, 68, 68, 0.05)' : 'rgba(30, 136, 229, 0.05)',
                    '& .menu-icon-box': {
                      backgroundColor: item.color,
                      color: '#fff',
                    }
                  }
                }}
              >
                <Box
                  className="menu-icon-box"
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '8px',
                    backgroundColor: `${item.color}15`,
                    color: item.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                    transition: 'all 0.2s ease',
                  }}
                >
                  <i className={`iconfont ${item.icon}`} style={{ fontSize: '1.1rem' }}></i>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: item.isLogout ? '#EF4444' : '#1E293B' }}>
                  {item.label}
                </Typography>
              </MenuItem>
            )
          ))}
        </Menu>
      </Box>
      <div style={{
        flex: 1,
        overflowY: 'hidden',
      }}><Outlet /></div>

      { /* Master Password Dialog */}
      <Dialog
        open={masterPassOpen}
        onClose={() => { }}
        PaperProps={{
          sx: {
            borderRadius: '28px',
            p: 1,
            maxWidth: '400px',
            width: '100%',
            overflow: 'visible'
          }
        }}
      >
        <DialogTitle sx={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 800,
          textAlign: 'center',
          pt: 4,
          pb: 1
        }}>
          {localization.strings.masterPassword}
        </DialogTitle>
        <DialogContent sx={{ px: 4, pb: 4 }}>
          <Typography variant="body2" sx={{ color: '#64748B', textAlign: 'center', mb: 4 }}>
            {localization.strings.masterPasswordDesc}
          </Typography>

          <Stack spacing={3}>
            <form autoComplete="on" action="javascript:void(0);">
              <FormInput
                type="password"
                start_icon="icon-lock"
                label={localization.strings.masterPassword}
                placeholder={localization.strings.inputMasterPasswordHint}
                error={masterPasswordErr}
                onChange={(event) => {
                  setMasterPassword(event.target.value)
                  setMasterPasswordErr('')
                }}
                onKeyDown={(e) => {
                  if (e.keyCode === 13) {
                    checkMasterPass()
                    return
                  }
                }}
              />
            </form>

            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <Button
                variant="text"
                fullWidth
                onClick={logout}
                sx={{
                  borderRadius: '14px',
                  py: 1.5,
                  fontWeight: 700,
                  color: '#64748B',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' }
                }}
              >
                {localization.strings.logout}
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={checkMasterPass}
                sx={{
                  borderRadius: '14px',
                  py: 1.5,
                  fontWeight: 700,
                  backgroundColor: '#1E88E5',
                  boxShadow: '0 8px 20px rgba(30, 136, 229, 0.2)',
                  textTransform: 'none',
                  '&:hover': { backgroundColor: '#1565C0' }
                }}
              >
                {localization.strings.confirm}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>

      { /* Pay Dialog */}
      <Dialog
        open={payStep > 0}
        onClose={() => setPayStep(0)}
        PaperProps={{
          sx: {
            borderRadius: '24px',
            p: 1,
            maxWidth: '440px',
            width: '100%'
          }
        }}
      >
        <DialogTitle sx={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 800,
          textAlign: 'center',
          pt: 3
        }}>
          {payStep === 1 ? localization.strings.goPay : payTitle}
        </DialogTitle>
        <DialogContent sx={{ pb: 4, px: 3 }}>
          {payStep === 1 && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Paper
                elevation={0}
                onClick={loadBinancePayUrl}
                sx={{
                  p: 3,
                  borderRadius: '20px',
                  border: '1px solid rgba(0,0,0,0.06)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  '&:hover': {
                    backgroundColor: 'rgba(243, 186, 47, 0.05)',
                    borderColor: '#F3BA2F',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(243, 186, 47, 0.1)'
                  }
                }}
              >
                <Box sx={{
                  width: 60, height: 60, borderRadius: '16px',
                  backgroundColor: '#fff', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  border: '1px solid rgba(243, 186, 47, 0.3)',
                  boxShadow: '0 4px 10px rgba(243, 186, 47, 0.1)'
                }}>
                  <img src={binanceLogo} width="40px" height="40px" alt="Binance" />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Binance Pay</Typography>
                  <Typography variant="body2" sx={{ color: '#94A3B8' }}>{localization.strings.easypassBinancePrice}</Typography>
                </Box>
              </Paper>

              <Paper
                elevation={0}
                onClick={loadWechatPayUrl}
                sx={{
                  p: 3,
                  borderRadius: '20px',
                  border: '1px solid rgba(0,0,0,0.06)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  '&:hover': {
                    backgroundColor: 'rgba(7, 193, 96, 0.05)',
                    borderColor: '#07C160',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(7, 193, 96, 0.1)'
                  }
                }}
              >
                <Box sx={{
                  width: 60, height: 60, borderRadius: '16px',
                  backgroundColor: '#fff', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  border: '1px solid rgba(7, 193, 96, 0.2)',
                  boxShadow: '0 4px 10px rgba(7, 193, 96, 0.1)'
                }}>
                  <img src={wechatLogo} width="40px" height="40px" alt="WeChat" />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>WeChat Pay</Typography>
                  <Typography variant="body2" sx={{ color: '#94A3B8' }}>{localization.strings.easypassWechatPrice}</Typography>
                </Box>
              </Paper>

              {/* Paddle Pay */}
              <Paper
                elevation={0}
                onClick={loadPaddlePayUrl}
                sx={{
                  p: 3,
                  borderRadius: '20px',
                  border: '1px solid rgba(0,0,0,0.06)',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 132, 255, 0.05)',
                    borderColor: '#0084FF',
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 24px rgba(0, 132, 255, 0.1)'
                  }
                }}
              >
                <Box sx={{
                  width: 60, height: 60, borderRadius: '16px',
                  backgroundColor: '#fff', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  border: '1px solid rgba(0, 132, 255, 0.2)',
                  boxShadow: '0 4px 10px rgba(0, 132, 255, 0.1)',
                  fontSize: '1.8rem',
                }}>
                  💳
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Paddle Pay</Typography>
                  <Typography variant="body2" sx={{ color: '#94A3B8' }}>{localization.strings.easypassPaddlePrice}</Typography>
                </Box>
              </Paper>
            </Stack>
          )}

          {payStep === 2 && (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress size="4rem" thickness={5} sx={{ color: '#1E88E5', mb: 3 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
                {localization.strings.loading}
              </Typography>
            </Box>
          )}

          {payStep === 3 && (
            <Fade in={true}>
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#1E293B', mb: 1 }}>
                    {payPrice}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B' }}>
                    {payHint}
                  </Typography>
                </Box>

                <Box sx={{
                  p: 2,
                  backgroundColor: '#fff',
                  borderRadius: '24px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                  border: '1px solid rgba(0,0,0,0.04)',
                  display: 'inline-block',
                  mb: 4
                }}>
                  <img src={payUrl} style={{ display: 'block', width: '220px', height: '220px' }} alt="QR Code" />
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    setPayStep(0)
                    ServerUtils.userInfo().then(async (resp) => {
                      if (!resp.ok) return;
                      let respJson = await resp.json();
                      if (respJson.errno !== 0) return;
                      UserUtils.setUserInfo(respJson.data);
                      setUserInfo(respJson.data);
                    })
                  }}
                  sx={{
                    borderRadius: '14px',
                    py: 1.8,
                    fontWeight: 700,
                    backgroundColor: '#1E88E5',
                    boxShadow: '0 8px 20px rgba(30, 136, 229, 0.2)'
                  }}
                >
                  {localization.strings.payed}
                </Button>
              </Box>
            </Fade>
          )}
        </DialogContent>
      </Dialog>

      <ModalUserInfo open={userInfoDialogOpen} onClose={() => {
        setUserInfoDialogOpen(false)
      }} />
      <ModalHelp open={helpDialogOpen} onClose={() => {
        setHelpDialogOpen(false)
      }} />
      <ModalFeedback open={feedbackDialogOpen} onClose={() => {
        setFeedbackDialogOpen(false)
      }} />
      <ModalChangeMasterPassword open={changeMasterPasswordDialogOpen} onClose={() => {
        setChangeMasterPasswordDialogOpen(false)
      }} logout={logout} />
      <ModalDashboard open={dashboardDialogOpen} onClose={() => {
        setDashboardDialogOpen(false)
      }} />
    </div>
  )
}

export default Main;